/**
 * Converts shoe sizes between different systems using predefined mappings.
 * @param {string} from - The source size system (e.g., 'EU', 'MP', 'UK', 'US-K', 'US-W', 'US-M').
 * @param {string} to - The target size system.
 * @param {number|string} size - The shoe size to convert.
 * @returns {number|string} - The converted shoe size or an error message if conversion is not possible.
 */
export const convertShoeSize = (
    from: string,
    to: string,
    size: number | string
): number | string => {
    // Complete shoe size conversion table
    const sizeTable: Array<any> = [
        {
            EU: 26,
            MP: 16,
            UK: "8.5K",
            "US-K": "9K",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 27,
            MP: 16.5,
            UK: "9.5K",
            "US-K": "10K",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 28,
            MP: 17,
            UK: "10K",
            "US-K": "10.5K",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 29,
            MP: 17.5,
            UK: "10.5K",
            "US-K": "11K",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 30,
            MP: 18.5,
            UK: "11.5K",
            "US-K": "12K",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 31,
            MP: 19,
            UK: "12.5K",
            "US-K": "13K",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 32,
            MP: 20,
            UK: "13.5K",
            "US-K": "1",
            "US-W": null,
            "US-M": null,
        },
        {
            EU: 33,
            MP: 20.5,
            UK: "1",
            "US-K": "1.5",
            "US-W": null,
            "US-M": null,
        },
        { EU: 34, MP: 21, UK: "2", "US-K": "2", "US-W": null, "US-M": null },
        {
            EU: 35,
            MP: 21.5,
            UK: "2.5",
            "US-K": "3",
            "US-W": null,
            "US-M": null,
        },
        { EU: 36, MP: 22, UK: "3", "US-K": "4", "US-W": "4.5", "US-M": null },
        {
            EU: 36.5,
            MP: 22.5,
            UK: "3.5",
            "US-K": "4",
            "US-W": "4.5",
            "US-M": null,
        },
        { EU: 37, MP: 23, UK: "4", "US-K": "5", "US-W": "5.5", "US-M": null },
        {
            EU: 37.5,
            MP: 23.5,
            UK: "4.5",
            "US-K": "5",
            "US-W": "6",
            "US-M": null,
        },
        { EU: 38, MP: 24, UK: "5", "US-K": "6", "US-W": "6.5", "US-M": null },
        {
            EU: 38.5,
            MP: 24,
            UK: "5",
            "US-K": null,
            "US-W": "6.5",
            "US-M": null,
        },
        { EU: 39, MP: 24.5, UK: "5.5", "US-K": null, "US-W": "7", "US-M": "6" },
        {
            EU: 39.5,
            MP: 25,
            UK: "6",
            "US-K": null,
            "US-W": "7.5",
            "US-M": "6.5",
        },
        { EU: 40, MP: 25.5, UK: "6.5", "US-K": null, "US-W": "8", "US-M": "7" },
        {
            EU: 40.5,
            MP: 25.5,
            UK: "7",
            "US-K": null,
            "US-W": "8.5",
            "US-M": "7.5",
        },
        { EU: 41, MP: 26.5, UK: "7.5", "US-K": null, "US-W": "9", "US-M": "8" },
        { EU: 41.5, MP: 26, UK: "7.5", "US-K": null, "US-W": "9", "US-M": "8" },
        { EU: 42, MP: 27, UK: "8", "US-K": null, "US-W": "9.5", "US-M": "8.5" },
        {
            EU: 42.5,
            MP: 27,
            UK: "8.5",
            "US-K": null,
            "US-W": "10",
            "US-M": "9",
        },
        {
            EU: 43,
            MP: 27.5,
            UK: "8.5",
            "US-K": null,
            "US-W": "10.5",
            "US-M": "9.5",
        },
        {
            EU: 43.5,
            MP: 27.5,
            UK: "9",
            "US-K": null,
            "US-W": "10.5",
            "US-M": "9.5",
        },
        { EU: 44, MP: 28, UK: "9.5", "US-K": null, "US-W": "10", "US-M": "11" },
        {
            EU: 44.5,
            MP: 28.5,
            UK: "10",
            "US-K": null,
            "US-W": "10.5",
            "US-M": "12",
        },
        {
            EU: 45,
            MP: 29,
            UK: "10.5",
            "US-K": null,
            "US-W": "10.5",
            "US-M": "12",
        },
        {
            EU: 45.5,
            MP: 29,
            UK: "10.5",
            "US-K": null,
            "US-W": null,
            "US-M": "11",
        },
        {
            EU: 46,
            MP: 29.5,
            UK: "11",
            "US-K": null,
            "US-W": null,
            "US-M": "11.5",
        },
        {
            EU: 46.5,
            MP: 30,
            UK: "11.5",
            "US-K": null,
            "US-W": null,
            "US-M": "12",
        },
        {
            EU: 47,
            MP: 30.5,
            UK: "12",
            "US-K": null,
            "US-W": null,
            "US-M": "12.5",
        },
        {
            EU: 48,
            MP: 31,
            UK: "12.5",
            "US-K": null,
            "US-W": null,
            "US-M": "13",
        },
        {
            EU: 49,
            MP: 32,
            UK: "13.5",
            "US-K": null,
            "US-W": null,
            "US-M": "14",
        },
        { EU: 50, MP: 33, UK: "14", "US-K": null, "US-W": null, "US-M": "15" },
        { EU: 51, MP: 34, UK: "15", "US-K": null, "US-W": null, "US-M": "16" },
    ]

    // Find the matching row for the source size
    const row = sizeTable.find((row) => {
        const rowValue = row[from]
        // Handle both numeric and string comparisons
        if (typeof size === "string" && typeof rowValue === "string") {
            return rowValue.toLowerCase() === size.toLowerCase()
        }
        if (typeof size === "number" && typeof rowValue === "number") {
            return Math.abs(rowValue - size) < 0.01 // Allow small floating point differences
        }
        return rowValue?.toString() === size?.toString()
    })

    if (!row) {
        return 0
    }

    const result = row[to]
    if (result === null || result === undefined) {
        return 0
    }

    return result
}

// Helper function to round to a specified number of decimal places
const round = (num: number, decimalPlaces: number): number => {
    const factor = Math.pow(10, decimalPlaces)
    return Math.round(num * factor) / factor
}

export const convertWeight = (
    value: string,
    unitLabel: string,
    targetUnit: string
): string => {
    const numValue = parseFloat(value)
    if (isNaN(numValue) || numValue === 0) return ""

    // Use more precise conversion factors and avoid intermediate rounding
    const kgToPound = 2.20462262185
    const kgToStone = 0.157473044
    const stoneToPound = 14

    let result: number

    switch (unitLabel) {
        case "Kg":
            if (targetUnit === "Stone") result = numValue * kgToStone
            else if (targetUnit === "Pound") result = numValue * kgToPound
            else result = numValue
            break
        case "Stone":
            if (targetUnit === "Kg") result = numValue / kgToStone
            else if (targetUnit === "Pound") result = numValue * stoneToPound
            else result = numValue
            break
        case "Pound":
            if (targetUnit === "Kg") result = numValue / kgToPound
            else if (targetUnit === "Stone") result = numValue / stoneToPound
            else result = numValue
            break
        default:
            result = numValue
    }

    // Round only at the end of conversion
    return round(result, 4).toFixed(0)
}

export const convertHeight = (
    value: string,
    unitLabel: string,
    targetUnit: string
): string => {
    const numValue = parseFloat(value)
    if (isNaN(numValue)) return value

    switch (unitLabel) {
        case "Cm":
            if (targetUnit === "Inch")
                return round(numValue * 0.393701, 4).toFixed(0)
            return value
        case "Inch":
            if (targetUnit === "Cm") return round(numValue * 2.54, 4).toFixed(0)
            return value
        default:
            return value
    }
}
